import type { ComponentType } from "react";
import { AskUserQuestionCard } from "./AskUserQuestionCard";
import type { ToolCallEntry } from "./useTurns";

// Props every human-in-the-loop tool card receives. The card reads the pending
// call from `entry` (interactive while `entry.result` is undefined, read-only
// once set) and submits the user's response via `onAnswer`, which appends a
// tool message and resumes the paused run.
export interface HitlToolProps {
  entry: ToolCallEntry;
  // Whether the agent run is still in flight. Lets a card tell "the tool-call
  // args are still streaming in" (show a placeholder) apart from "the args are
  // final but unparseable" (a real error to surface), rather than guessing.
  isRunning: boolean;
  // Resume the paused run with the user's response. Omitted in read-only
  // views (shared / admin), where the card renders but cannot be answered.
  // Resolves when the resume is sent; rejects if it fails, so the card can
  // re-enable Submit for a retry rather than latching on "Submitting…".
  onAnswer?: (toolCallId: string, answer: string) => void | Promise<void>;
}

// Registry of human-in-the-loop tools: backend tools that end the run
// un-resulted (a deferred/external tool call) and require a user response
// before the agent can continue. Maps the backend tool name to the card that
// collects that response.
//
// This is the single place HITL tool names live. The turn-rendering split and
// the composer block are otherwise name-agnostic — they ask `isHitlTool` and
// look the card up here — so adding a new HITL tool is just one entry.
export const HITL_TOOL_RENDERERS: Record<string, ComponentType<HitlToolProps>> = {
  ask_user_question: AskUserQuestionCard,
};

// Whether a tool name is a registered HITL tool. Used to pull interactive
// cards out of the read-only tool disclosure and to block the composer while
// an answer is outstanding.
export const isHitlTool = (name: string): boolean =>
  Object.prototype.hasOwnProperty.call(HITL_TOOL_RENDERERS, name);
